// Malakh, SiliconBlast's email. Rhema saves drafts there over JMAP and sends one only when the
// person presses Send. An email leaves Rhema's end-to-end encryption, so the app says so first.

import { LinkError, type LinksApi } from "./api";

const CORE = "urn:ietf:params:jmap:core";
const MAIL = "urn:ietf:params:jmap:mail";
const SUBMISSION = "urn:ietf:params:jmap:submission";
/** Every request names submission too, as RFC 8620 asks of a client that may send. */
const USING = [CORE, MAIL, SUBMISSION];

/** Malakh takes up to 20 attachments and 25 MB in all. */
export const MAX_EMAIL_ATTACHMENTS = 20;
export const MAX_EMAIL_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export type JmapSession = {
  apiUrl: string;
  uploadUrl?: string;
  accounts: Record<string, { name: string }>;
  primaryAccounts: Record<string, string>;
  username?: string;
};

export type JmapIdentity = { id: string; name: string; email: string };
export type JmapMailbox = { id: string; name: string; role: string | null };
export type JmapBlob = { accountId: string; blobId: string; type: string; size: number };

export type EmailAttachment = { blob: Blob; name: string; type: string };

export type EmailDraftInput = {
  to: string[];
  subject: string;
  body: string;
  attachments?: EmailAttachment[];
};

type MethodCall = [string, Record<string, unknown>, string];

const EMAIL = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

export function isEmailAddress(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** Splits "a@x.org, b@y.org" into addresses. */
export function parseAddresses(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The gateway path for a URL from the JMAP session. Malakh gives paths, but a full URL works too. */
function upstreamPath(url: string): string {
  if (/^https?:\/\//.test(url)) {
    const u = new URL(url);
    return u.pathname + u.search;
  }
  return url;
}

function noIdentity(): LinkError {
  return new LinkError("failed", 0, "malakh", "Your Malakh mailbox has no address to send from.");
}

export class MalakhLink {
  private session: Promise<JmapSession> | null = null;
  /** The address drafts are sent from, once known. */
  private sender: JmapIdentity | null = null;

  constructor(private readonly links: LinksApi) {}

  /** The JMAP session, fetched once. */
  getSession(): Promise<JmapSession> {
    this.session ??= this.links
      .gatewayJson<JmapSession>("malakh", "GET", "/.well-known/jmap")
      .catch((err) => {
        this.session = null;
        throw err;
      });
    return this.session;
  }

  async accountId(): Promise<string> {
    const s = await this.getSession();
    const id = s.primaryAccounts[MAIL] ?? s.primaryAccounts[CORE] ?? Object.keys(s.accounts)[0];
    if (!id) throw new LinkError("failed", 0, "malakh", "Your Malakh account has no mailbox.");
    return id;
  }

  /** Runs JMAP method calls and returns their responses by call id. A method error throws. */
  async call(calls: MethodCall[]): Promise<Record<string, Record<string, unknown>>> {
    const s = await this.getSession();
    const data = await this.links.gatewayJson<{
      methodResponses: [string, Record<string, unknown>, string][];
    }>("malakh", "POST", upstreamPath(s.apiUrl || "/jmap/"), { json: { using: USING, methodCalls: calls } });
    const out: Record<string, Record<string, unknown>> = {};
    for (const [name, args, id] of data.methodResponses ?? []) {
      if (name === "error") {
        const description = typeof args.description === "string" ? args.description : undefined;
        throw new LinkError("invalid", 400, "malakh", description ?? "Malakh did not accept that.");
      }
      out[id] = args;
    }
    return out;
  }

  async identities(): Promise<JmapIdentity[]> {
    const accountId = await this.accountId();
    const res = await this.call([["Identity/get", { accountId }, "i"]]);
    return (res.i?.list as JmapIdentity[] | undefined) ?? [];
  }

  async mailboxes(): Promise<JmapMailbox[]> {
    const accountId = await this.accountId();
    const res = await this.call([["Mailbox/get", { accountId }, "m"]]);
    return (res.m?.list as JmapMailbox[] | undefined) ?? [];
  }

  /** Uploads one attachment and returns its blob. The bytes go through the gateway to Malakh. */
  async upload(file: EmailAttachment): Promise<JmapBlob> {
    const s = await this.getSession();
    const accountId = await this.accountId();
    // The session names the upload URL, such as /jmap/upload/{accountId}/. /upload is the fallback.
    const path = s.uploadUrl
      ? upstreamPath(s.uploadUrl.replace("{accountId}", encodeURIComponent(accountId)))
      : "/upload";
    const res = await this.links.gateway("malakh", "POST", path, {
      body: file.blob,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
    return (await res.json()) as JmapBlob;
  }

  /**
   * Saves a draft. It goes into the Drafts mailbox and is never sent from here. Returns the email id
   * that send() needs.
   */
  async createDraft(input: EmailDraftInput): Promise<{ emailId: string }> {
    const attachments = input.attachments ?? [];
    if (attachments.length > MAX_EMAIL_ATTACHMENTS)
      throw new LinkError(
        "invalid",
        400,
        "malakh",
        `An email can carry up to ${MAX_EMAIL_ATTACHMENTS} files.`,
      );
    const total = attachments.reduce((n, a) => n + a.blob.size, 0);
    if (total > MAX_EMAIL_ATTACHMENT_BYTES)
      throw new LinkError("invalid", 413, "malakh", "Attachments can be up to 25 MB in all.");
    const bad = input.to.find((a) => !isEmailAddress(a));
    if (bad) throw new LinkError("invalid", 400, "malakh", `${bad} is not an email address.`);

    const accountId = await this.accountId();
    // One request for both lookups. The sender is kept for send().
    const looked = await this.call([
      ["Mailbox/get", { accountId }, "m"],
      ["Identity/get", { accountId }, "i"],
    ]);
    const mailboxes = (looked.m?.list as JmapMailbox[] | undefined) ?? [];
    const identities = (looked.i?.list as JmapIdentity[] | undefined) ?? [];
    // The From is always one of the mailbox's own identities. The SBI sign-in address can differ
    // from every address Malakh lets this mailbox send as, so it is never used.
    const from = identities[0];
    if (!from) throw noIdentity();
    this.sender = from;
    const blobs: JmapBlob[] = [];
    for (const a of attachments) blobs.push(await this.upload(a));
    const drafts = mailboxes.find((m) => m.role === "drafts");

    const email: Record<string, unknown> = {
      ...(drafts ? { mailboxIds: { [drafts.id]: true } } : {}),
      keywords: { $draft: true, $seen: true },
      from: [{ name: from.name || undefined, email: from.email }],
      to: input.to.map((email) => ({ email: email.trim() })),
      subject: input.subject,
      // Standard JMAP reads the body through bodyValues. Malakh reads the value on the part.
      textBody: [{ partId: "body", type: "text/plain", value: input.body }],
      bodyValues: { body: { value: input.body } },
      attachments: blobs.map((b, i) => ({
        blobId: b.blobId,
        type: attachments[i]?.type || b.type,
        name: attachments[i]?.name || "attachment",
        disposition: "attachment",
      })),
    };
    const res = await this.call([["Email/set", { accountId, create: { draft: email } }, "e"]]);
    const created = (res.e?.created as Record<string, { id: string }> | undefined)?.draft;
    if (!created) {
      const why = (res.e?.notCreated as Record<string, { description?: string }> | undefined)?.draft;
      throw new LinkError("invalid", 400, "malakh", why?.description ?? "Malakh did not save the draft.");
    }
    return { emailId: created.id };
  }

  /** Sends a saved draft. Only call this from an explicit Send. */
  async send(emailId: string, to: string[]): Promise<void> {
    const accountId = await this.accountId();
    this.sender ??= (await this.identities())[0] ?? null;
    const sender = this.sender;
    if (!sender) throw noIdentity();
    const res = await this.call([
      [
        "EmailSubmission/set",
        {
          accountId,
          create: {
            send: {
              emailId,
              identityId: sender.id,
              envelope: {
                mailFrom: { email: sender.email },
                rcptTo: to.map((email) => ({ email: email.trim() })),
              },
            },
          },
        },
        "s",
      ],
    ]);
    const created = (res.s?.created as Record<string, unknown> | undefined)?.send;
    if (!created) {
      const why = (res.s?.notCreated as Record<string, { description?: string }> | undefined)?.send;
      throw new LinkError("invalid", 400, "malakh", why?.description ?? "Malakh did not send the email.");
    }
  }
}
